export interface LoadableAmpli<TLoadOptions> {
  flush(): Promise<void>;
  load(options: TLoadOptions): void;
}

export type ConfigWithRequiredServerUrl<TConfig extends { serverUrl?: string }> = Omit<
  TConfig,
  'serverUrl'
> & {
  serverUrl: string;
};

export type LoadOptionsWithRequiredServerUrl<
  TClient,
  TConfig extends { serverUrl?: string },
  TEnvironment extends string,
> =
  | {
      disabled?: boolean;
      environment: TEnvironment;
      client: {
        configuration: ConfigWithRequiredServerUrl<TConfig>;
      };
    }
  | {
      disabled?: boolean;
      client: {
        apiKey: string;
        configuration: ConfigWithRequiredServerUrl<TConfig>;
      };
    }
  | {
      disabled?: boolean;
      client: {
        instance: TClient;
      };
    };

export interface ManagedAmpliOptions<TLoadOptions, TAmpli extends LoadableAmpli<TLoadOptions>> {
  ampli: TAmpli;
  loadOptions: TLoadOptions;
}

export class ManagedAmpliClient<TLoadOptions, TAmpli extends LoadableAmpli<TLoadOptions>> {
  readonly #ampli: TAmpli;
  readonly #loadOptions: TLoadOptions;
  #loaded = false;

  constructor({ ampli, loadOptions }: ManagedAmpliOptions<TLoadOptions, TAmpli>) {
    this.#ampli = ampli;
    this.#loadOptions = loadOptions;
  }

  get client(): TAmpli {
    return this.#ampli;
  }

  get isLoaded(): boolean {
    return this.#loaded;
  }

  load(): TAmpli {
    if (!this.#loaded) {
      this.#ampli.load(this.#loadOptions);
      this.#loaded = true;
    }

    return this.#ampli;
  }

  flush(): Promise<void> {
    return this.#ampli.flush();
  }
}

export function assertLoadOptionsServerUrl(
  options: {
    client?: {
      configuration?: {
        serverUrl?: string;
      };
      instance?: unknown;
    };
  },
  runtime: 'browser' | 'node',
): void {
  if (options.client?.instance) {
    return;
  }

  const serverUrl = options.client?.configuration?.serverUrl;
  if (!serverUrl?.trim()) {
    throw new Error(
      `@blinq/analytics/${runtime} requires client.configuration.serverUrl so events go through the analytics proxy`,
    );
  }
}
